import { useEffect, useState } from "react";
import * as D from "@radix-ui/react-dialog";
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";
import { IconButton } from "@dfws/ui";
import s from "./Lightbox.module.css";

export interface LightboxItem {
  index: number;
  src: string;
  download: string;
  name: string;
  size: string;
}

/** Full-screen image preview with previous/next (arrow keys), download and Esc to close. */
export function Lightbox({ items, index, onClose }: { items: LightboxItem[]; index: number; onClose: () => void }) {
  const [i, setI] = useState(index);
  const item = items[i];
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") setI((x) => Math.min(items.length - 1, x + 1));
      if (e.key === "ArrowLeft") setI((x) => Math.max(0, x - 1));
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [items.length]);
  if (!item) return null;
  return (
    <D.Root open onOpenChange={(o) => !o && onClose()}>
      <D.Portal>
        <D.Overlay className={s.overlay} />
        <D.Content className={s.content} aria-describedby={undefined}>
          <header className={s.bar}>
            <D.Title className={s.title}>{item.name}</D.Title>
            <span className={s.meta}>
              {item.size}
              {items.length > 1 && ` · ${i + 1} of ${items.length}`}
            </span>
            <a className={s.action} href={item.download} download={item.name} aria-label={`Download ${item.name}`}>
              <Download />
            </a>
            <D.Close asChild>
              <IconButton label="Close" shortcut="Esc" icon={<X />} className={s.close} />
            </D.Close>
          </header>
          <div className={s.stage}>
            {i > 0 && <IconButton label="Previous" shortcut="←" icon={<ChevronLeft />} className={s.prev} onClick={() => setI(i - 1)} />}
            <img key={item.src} src={item.src} alt={item.name} className={s.image} />
            {i < items.length - 1 && <IconButton label="Next" shortcut="→" icon={<ChevronRight />} className={s.next} onClick={() => setI(i + 1)} />}
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
